// LivePrices.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, BarChart, Gem, Scale, MapPin } from 'lucide-react'; // Removed DollarSign

const LivePrices: React.FC = () => {
  const [selectedCity, setSelectedCity] = useState<string>('Delhi');
  const [gold24KPrice, setGold24KPrice] = useState<number | null>(null);
  const [gold22KPrice, setGold22KPrice] = useState<number | null>(null);
  const [gold18KPrice, setGold18KPrice] = useState<number | null>(null);
  const [silverPriceKg, setSilverPriceKg] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Define a list of Indian cities for the dropdown
  const indianCities = [
    'Delhi', 'Mumbai', 'Chennai', 'Kolkata', 'Hyderabad', 'Bengaluru',
    'Ahmedabad', 'Pune', 'Jaipur', 'Lucknow', 'Kanpur', 'Nagpur', 'Indore'
  ];

  // Simulated price data for different cities (replace with actual API fetch)
  // In a real application, you'd fetch this from a backend or a reliable public API.
  const getSimulatedPrices = useCallback((city: string) => {
    // These are base prices per gram for gold and per 1 kg for silver
    let baseGold24K_per_gram = 11080; // Adjusted for per gram, reflecting ~72000 per 10g
    let baseSilverKg = 90000; // Still per kg for silver

    // Simulate price variations based on city
    switch (city) {
      case 'Delhi':
        baseGold24K_per_gram = 10500 + Math.random() * 50; // Delhi slightly higher, reflecting current rates
        baseSilverKg = 90000 - Math.random() * 200;
        break;
      case 'Mumbai':
        baseGold24K_per_gram = 10000 + Math.random() * 60;
        baseSilverKg = 90200 + Math.random() * 150;
        break;
      case 'Chennai':
        baseGold24K_per_gram = 11000 + Math.random() * 40;
        baseSilverKg = 89800 - Math.random() * 100;
        break;
      case 'Kolkata':
        baseGold24K_per_gram = 10980 + Math.random() * 55;
        baseSilverKg = 90100 + Math.random() * 180;
        break;
      case 'Hyderabad':
        baseGold24K_per_gram = 10550 + Math.random() * 45;
        baseSilverKg = 89900 - Math.random() * 250;
        break;
      default: // For other cities, a general range
        baseGold24K_per_gram = 10150 + Math.random() * 70;
        baseSilverKg = 89700 + Math.random() * 300;
        break;
    }

    // Apply a small fluctuation for "live" feel
    const goldFluctuation = (Math.random() * 0.0002 - 0.0001) * baseGold24K_per_gram; // +/- 0.01%
    const silverFluctuation = (Math.random() * 0.0005 - 0.00025) * baseSilverKg; // +/- 0.025%

    const newGold24K = parseFloat((baseGold24K_per_gram + goldFluctuation).toFixed(2));
    const newSilverKg = parseFloat((baseSilverKg + silverFluctuation).toFixed(2));

    return {
      gold24K: newGold24K,
      gold22K: parseFloat((newGold24K * (22 / 24)).toFixed(2)),
      gold18K: parseFloat((newGold24K * (18 / 24)).toFixed(2)),
      silverKg: newSilverKg,
    };
  }, []);

  const fetchPrices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // In a real scenario, you would make an API call here to a service like Metals-API.
      // Example with a hypothetical API call structure (you'll need a real API key and endpoint):
      /*
      const apiKey = ""; // Your actual API key from a service like Metals-API
      // Note: Metals-API offers city-specific rates for India for XAU and XAG.
      // E.g., for Delhi Gold 24K: XAU-DELH, for Mumbai Silver: XAG-MUMB
      const goldSymbol = `XAU-${selectedCity.toUpperCase().substring(0,4)}`; // Adjust based on API docs
      const silverSymbol = `XAG-${selectedCity.toUpperCase().substring(0,4)}`; // Adjust based on API docs

      const apiUrl = `https://api.metals-api.com/v1/latest?access_key=${apiKey}&base=INR&symbols=${goldSymbol},${silverSymbol}`;

      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`API Error: ${response.statusText}`);
      }
      const data = await response.json();

      // Assuming API returns price per unit (e.g., per gram for gold, per kg for silver)
      // You would parse data here, for example:
      // const actualGold24K = data.rates[goldSymbol]; // Get the 24K price per gram
      // const actualSilverKg = data.rates[silverSymbol]; // Get the silver price per kg

      // setGold24KPrice(actualGold24K);
      // setGold22KPrice(actualGold24K * (22/24));
      // setGold18KPrice(actualGold24K * (18/24));
      // setSilverPriceKg(actualSilverKg);

      // For now, using simulated prices:
      */
      const simulatedData = getSimulatedPrices(selectedCity);
      setGold24KPrice(simulatedData.gold24K);
      setGold22KPrice(simulatedData.gold22K);
      setGold18KPrice(simulatedData.gold18K);
      setSilverPriceKg(simulatedData.silverKg);

    } catch (err: any) {
      console.error("Failed to fetch prices:", err);
      setError(`Failed to fetch prices: ${err.message}. Showing simulated data.`);
      // Fallback to simulated data if API fails
      const simulatedData = getSimulatedPrices(selectedCity);
      setGold24KPrice(simulatedData.gold24K);
      setGold22KPrice(simulatedData.gold22K);
      setGold18KPrice(simulatedData.gold18K);
      setSilverPriceKg(simulatedData.silverKg);
    } finally {
      setLoading(false);
    }
  }, [selectedCity, getSimulatedPrices]);

  // Initial fetch and update interval
  useEffect(() => {
    fetchPrices(); // Fetch prices on component mount and when city changes
    const intervalId = setInterval(fetchPrices, 10000); // Update every 10 seconds
    return () => clearInterval(intervalId); // Clear interval on unmount
  }, [fetchPrices]);

  // Helper to format currency
  const formatCurrency = (amount: number | null) => {
    if (amount === null) return 'N/A';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-8 flex flex-col items-center">
      <div className="bg-white rounded-xl shadow-2xl p-8 max-w-4xl w-full my-8">
        <h1 className="text-4xl font-extrabold text-center text-gray-900 mb-4 flex items-center justify-center">
          <Sparkles className="w-10 h-10 text-yellow-500 mr-3 animate-pulse" />
          Live Precious Metal Prices
        </h1>
        <p className="text-center text-gray-600 mb-6 text-lg">
          Daily price updates for Gold (per gram) and Silver (per 1 kg).
        </p>

        {/* City Selection */}
        <div className="mb-8 flex flex-col sm:flex-row items-center justify-center gap-4">
          <label htmlFor="city-select" className="text-lg font-medium text-gray-700 flex items-center">
            <MapPin className="w-5 h-5 mr-2 text-gray-500" />
            Select City:
          </label>
          <select
            id="city-select"
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:border-yellow-500 focus:ring-yellow-500 text-gray-800"
            value={selectedCity}
            onChange={(e) => setSelectedCity(e.target.value)}
          >
            {indianCities.map((city) => (
              <option key={city} value={city}>{city}</option>
            ))}
          </select>
        </div>

        {/* Loading and Error Messages */}
        {loading && (
          <div className="text-center text-blue-600 mb-4 flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Fetching prices...
          </div>
        )}
        {error && (
          <div className="text-center text-red-600 mb-4">
            {error}
          </div>
        )}

        <p className="text-center text-gray-500 mb-8 text-sm italic">
          (Prices displayed are simulated for demonstration. Real-time API integration with a valid key would provide actual market rates.)
        </p>

        {/* Gold Prices Section */}
        <div className="mb-10">
          <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
            <Gem className="w-8 h-8 text-yellow-600 mr-3" />
            Gold Prices (per gram)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* 24K Gold Card */}
            <div className="bg-yellow-100 p-6 rounded-lg shadow-md border-2 border-yellow-300 flex flex-col items-center justify-between transition-transform transform hover:scale-105">
              <h3 className="text-2xl font-bold text-yellow-800 mb-3">24 Karat (99.9% Pure)</h3>
              <p className="text-4xl font-extrabold text-yellow-700 mb-4 flex items-center">
                {/* Removed DollarSign icon */}
                {formatCurrency(gold24KPrice)}
              </p>
              <span className="text-sm text-gray-600">Pure Gold</span>
            </div>

            {/* 22K Gold Card */}
            <div className="bg-yellow-50 p-6 rounded-lg shadow-md border-2 border-yellow-200 flex flex-col items-center justify-between transition-transform transform hover:scale-105">
              <h3 className="text-2xl font-bold text-yellow-700 mb-3">22 Karat (91.6% Pure)</h3>
              <p className="text-4xl font-extrabold text-yellow-600 mb-4 flex items-center">
                {/* Removed DollarSign icon */}
                {formatCurrency(gold22KPrice)}
              </p>
              <span className="text-sm text-gray-600">Jewellery Gold</span>
            </div>

            {/* 18K Gold Card */}
            <div className="bg-yellow-50 p-6 rounded-lg shadow-md border-2 border-yellow-100 flex flex-col items-center justify-between transition-transform transform hover:scale-105">
              <h3 className="text-2xl font-bold text-yellow-600 mb-3">18 Karat (75% Pure)</h3>
              <p className="text-4xl font-extrabold text-yellow-500 mb-4 flex items-center">
                {/* Removed DollarSign icon */}
                {formatCurrency(gold18KPrice)}
              </p>
              <span className="text-sm text-gray-600">Durable Jewellery</span>
            </div>
          </div>
        </div>

        {/* Silver Prices Section */}
        <div>
          <h2 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
            <Scale className="w-8 h-8 text-blue-600 mr-3" />
            Silver Prices (per 1 kg)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Silver Price Card */}
            <div className="bg-blue-100 p-6 rounded-lg shadow-md border-2 border-blue-300 flex flex-col items-center justify-between transition-transform transform hover:scale-105">
              <h3 className="text-2xl font-bold text-blue-800 mb-3">Fine Silver (99.9% Pure)</h3>
              <p className="text-4xl font-extrabold text-blue-700 mb-4 flex items-center">
                {/* Removed DollarSign icon */}
                {formatCurrency(silverPriceKg)}
              </p>
              <span className="text-sm text-gray-600">Industrial & Investment Use</span>
            </div>
            {/* Placeholder for future expansion or comparison */}
            <div className="bg-blue-50 p-6 rounded-lg shadow-md border-2 border-blue-200 flex flex-col items-center justify-center text-gray-500 italic transition-transform transform hover:scale-105">
              <BarChart className="w-12 h-12 mb-3 text-gray-400" />
              <p className="text-xl">Additional price data coming soon!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LivePrices;
